import React from 'react';
import ReactMarkdown from 'react-markdown';

import hexRgb from 'hex-rgb';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';

import ScrollReveal from '@components/common/ScrollReveal';
import { Container } from '@components/parts/Container';
import { Link } from '@components/parts/Link';

import { LinkProps } from '@customTypes/Link';
import { UploadedImage } from '@customTypes/UploadedImage';

type Props = {
  titleHint: string;
  title: string;
  description?: string;
  backgroundColor?: string;
  color?: string;
  rightImage?: UploadedImage;
  action?: LinkProps;
  className?: string;
};

const Wrapper = styled.div`
  padding-top: 90px;
  div.up {
    display: flex;
    flex-direction: column;
    .link {
      display: none;
    }
    div.image-container {
      flex: 1;
      text-align: right;

      img {
        padding: 50px 0;
        max-width: 100%;
      }
    }
  }
  @media (min-width: 992px) {
    div.up {
      flex-direction: row;
      .link {
        display: inline-flex;
        &.btn {
          font-size: 18px;
        }
      }
      div.image-container {
        img {
          margin-bottom: 160px;
          padding-top: 0;
          padding-left: 100px;
        }
      }
    }
    div.down {
      .link {
        display: none;
      }
    }
    padding-top: 160px;
    .link {
      margin-top: 40px;
    }
  }
  h1,
  h2,
  h3 {
    font-weight: 700;
    white-space: pre;
    @media (max-width: 768px) {
      white-space: pre-wrap;
    }
  }
  .title-hint {
    margin-bottom: 12px;
    font-size: 16px;
    font-weight: 500;
    opacity: 0.6;
    @media (min-width: 992px) {
      font-size: 24px;
    }
  }
  .link {
    margin-top: 30px;
  }
  p {
    color: rgba(0, 0, 0, 0.8);
    color: rgba(var(--text-color), 0.4);
    font-weight: 700;
    strong {
      color: rgba(0, 0, 0);
      color: rgba(var(--text-color), 1);
    }
    margin-top: 0.5em;
    font-size: 20px;
    @media (min-width: 992px) {
      font-size: 26px;
    }
  }
`;

export const Title: React.FC<Props> = (props) => {
  const {
    // prettier-ignore
    titleHint,
    title,
    description,
    backgroundColor: $backgroundColor,
    color: $color,
    rightImage,
    action,
    className,
  } = props;
  const backgroundColor = $backgroundColor ?? '#fff';
  const color = $color ?? '#000';
  const textColor = hexRgb(color);
  const textColorRgb = `${textColor.red},${textColor.green},${textColor.blue}`;

  return (
    // @ts-ignore
    <Container className={className} style={{ backgroundColor, color, '--text-color': textColorRgb }}>
      <Wrapper>
        <div className="up">
          {titleHint ? (
            <ScrollReveal>
              <div className="title-hint">{titleHint}</div>
              <h3>{title}</h3>
              {description ? (
                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                  {description}
                </ReactMarkdown>
              ) : null}
              {action ? <Link {...action} /> : null}
            </ScrollReveal>
          ) : (
            <ScrollReveal>
              <h2>{title}</h2>
              {description ? (
                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                  {description}
                </ReactMarkdown>
              ) : null}
            </ScrollReveal>
          )}
          {rightImage?.data?.attributes.url ? (
            <ScrollReveal intervalDelay={500} className="image-container">
              <img src={rightImage.data.attributes.url} alt={title} />
            </ScrollReveal>
          ) : null}
        </div>
        <div className="down">{action ? <Link {...action} /> : null}</div>
      </Wrapper>
    </Container>
  );
};
